import React, { useState, useEffect, useMemo } from "react";
import { Send, User, Clock, AlertCircle, Trophy, Sparkles } from "lucide-react";
import { Modal } from "../common/Modal";
import { Opportunity, Requirement, Answer } from "../../types";
import { useStaffDirectory } from "../../services/staffService";
import { createSmeTask } from "../../services/smeTasksService";
import { subscribeToAnswers } from "../../services/answersService";
import { calculateTextSimilarity } from "../../utils/textSimilarity";

interface AskSmeModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity;
  requirement: Requirement;
  initialQuestion?: string;
  onTaskCreated?: (taskId: string) => void;
}

export const AskSmeModal: React.FC<AskSmeModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  requirement,
  initialQuestion,
  onTaskCreated,
}) => {
  const [showAllCompanyStaff, setShowAllCompanyStaff] = useState(false);
  const { staff, allStaff } = useStaffDirectory({ rfpOnly: !showAllCompanyStaff });
  const activeStaffList = showAllCompanyStaff ? allStaff : staff;

  const [selectedStaffEmail, setSelectedStaffEmail] = useState(
    requirement.assignedToEmail || activeStaffList[0]?.email || "",
  );
  const [prevInitialQuestion, setPrevInitialQuestion] = useState(initialQuestion);
  const [question, setQuestion] = useState(initialQuestion || `Regarding "${requirement.title}": `);
  const [libraryAnswers, setLibraryAnswers] = useState<Answer[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    const unsub = subscribeToAnswers(
      (items) => setLibraryAnswers(items.filter((a) => a.status !== "retired")),
      (err) => console.error("Error fetching library answers for SME match:", err),
    );
    return () => unsub();
  }, [isOpen]);

  const libraryMatches = useMemo(() => {
    if (libraryAnswers.length === 0) return [];
    const queryText = `${requirement.title} ${requirement.prompt} ${question}`;
    const scored = libraryAnswers
      .map((ans) => {
        const sim = calculateTextSimilarity(queryText, `${ans.title} ${ans.body}`);
        return {
          answer: ans,
          score: sim,
        };
      })
      .filter((item) => item.score >= 0.5)
      .sort((a, b) => b.score - a.score);

    return scored.slice(0, 3);
  }, [libraryAnswers, requirement.title, requirement.prompt, question]);

  if (initialQuestion !== prevInitialQuestion) {
    setPrevInitialQuestion(initialQuestion);
    setQuestion(initialQuestion || `Regarding "${requirement.title}": `);
  }
  const [dueDate, setDueDate] = useState(() => {
    // Default to 4 days from now or the day before proposal due date
    const d = new Date();
    d.setDate(d.getDate() + 4);
    return d.toISOString().slice(0, 10);
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedStaff =
    activeStaffList.find((s) => s.email === selectedStaffEmail) || activeStaffList[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStaff) {
      setError("Please select a subject-matter expert.");
      return;
    }
    if (!question.trim()) {
      setError("Please enter a specific question for the SME.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const taskId = await createSmeTask(opportunity.id, {
        opportunityTitle: opportunity.title,
        clientName: opportunity.client,
        requirementId: requirement.id,
        requirementTitle: requirement.title,
        requirementText: requirement.prompt,
        question: question.trim(),
        assigneeId: selectedStaff.id,
        assigneeName: selectedStaff.displayName,
        assigneeEmail: selectedStaff.email,
        dueDate: dueDate || undefined,
      });

      if (onTaskCreated) {
        onTaskCreated(taskId);
      }
      onClose();
    } catch (err) {
      console.error("Failed to create SME task:", err);
      setError(err instanceof Error ? err.message : "Failed to route SME task.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ask a Subject Matter Expert"
      description={`Route requirement to a team expert for technical input, volume validation, or specialized drafting.`}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-rose-50 p-2.5 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Target Requirement Summary */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900/40">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
            Target Requirement
          </span>
          <span className="mt-0.5 block text-xs font-semibold text-slate-800 dark:text-slate-200">
            {requirement.title}
          </span>
          <p className="mt-1 line-clamp-3 text-xs text-slate-600 dark:text-slate-400">
            {requirement.prompt}
          </p>
        </div>

        {/* Similar Library Answer Match Warning */}
        {libraryMatches.length > 0 && libraryMatches[0] && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40 space-y-2">
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-amber-900 dark:text-amber-200">
                    Similar Library Answer Found ({Math.round(libraryMatches[0].score * 100)}%
                    match)
                  </span>
                  {(libraryMatches[0].answer.wonBidCount || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-200/80 px-1.5 py-0.5 text-[10px] font-bold text-amber-900 dark:bg-amber-900 dark:text-amber-200">
                      <Trophy className="h-3 w-3" />
                      <span>Won ({libraryMatches[0].answer.wonBidCount})</span>
                    </span>
                  )}
                </div>
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                  {libraryMatches[0].answer.title}
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                  {libraryMatches[0].answer.body}
                </p>
                <p className="text-[11px] text-amber-800/80 dark:text-amber-300/80">
                  An approved answer may already address this topic. You can proceed with routing to
                  the SME anyway or use the library response.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SME Picker */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Assign To (MLC Staff HQ):
          </label>
          <div className="relative mt-1">
            <select
              value={selectedStaffEmail}
              onChange={(e) => setSelectedStaffEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-8 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {activeStaffList.map((s) => (
                <option key={s.id} value={s.email}>
                  {s.displayName} ({s.department || s.title || "Staff"}
                  {s.role ? ` • ${s.role}` : ""}) - {s.email}
                </option>
              ))}
            </select>
            <User className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
          </div>

          <div className="mt-1.5 flex items-center justify-between">
            <label className="flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllCompanyStaff}
                onChange={(e) => setShowAllCompanyStaff(e.target.checked)}
                className="rounded border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
              />
              <span>Show all company staff from Staff HQ (for one-off specialized SMEs)</span>
            </label>
            <span className="text-[10px] text-slate-400">
              {activeStaffList.length} {showAllCompanyStaff ? "all staff" : "RFP team"}
            </span>
          </div>
        </div>

        {/* Question Textarea */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Specific Question / Request for Information:
          </label>
          <textarea
            rows={4}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Can we commit to delivering 5,000 words in 24 hours for this language pair? What is our active court-certified roster size?"
            className="mt-1 w-full rounded-lg border border-slate-300 p-2.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        {/* Due Date */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
            Target Due Date for SME:
          </label>
          <div className="relative mt-1">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-8 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
            <Clock className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="btn-animate rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !question.trim()}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#238a97] disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" />
            <span>
              {isSubmitting
                ? "Routing..."
                : libraryMatches.length > 0
                  ? "Ask SME Anyway"
                  : "Route to SME"}
            </span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
